import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { tf, type TfKey } from '../../copyTaste'
import { f2 } from '../../copyFlow2'
import type { Card } from '../../lib/browse'
import { NFM_REASONS, type NfmReason } from '../../lib/checkinCore'
import { dismissLast, giveReason, justHid, mark, notForMe, retryLast, undoLast, unmark, useTaste, type Ctx } from '../../lib/tasteFeedback'
import { lf } from '../../copyLaneF'
import { Check, Close } from '../../ui/icons'
import { FADE, spring } from '../../ui/motion'
import { Spinner } from '../../ui/Spinner'
import { buttonClass } from '../../ui/styles'
import { fb } from '../../copyFeatBrowse'
import { useFlag } from '../../lib/flags'
import { hideKind, kindOf, unhideKind, useWords } from '../../lib/words'

/**
 * More like this / Less like this / Not for me (R-14 app half), under Save on every card and on the activity's detail.
 * `full`: the detail sheet's wording (room for "More like this"); cards use the short labels so all three fit one line at
 * 375 px. Not for me takes the card out of every list at once (lib/tasteFeedback.ts); `onGone` closes the detail.
 */
export function TasteActions({ card, ctx, full = false, onGone }: { card: Card; ctx: Ctx; full?: boolean; onGone?: () => void }) {
  const { marks } = useTaste()
  const m = marks[card.item.id]
  const [err, setErr] = useState(false)
  const tap = (which: 'more' | 'less') => {
    if (justHid()) return
    setErr(false)
    // B-155: a selected one stays selected (a fill, not a "Got it" line); a second tap takes it back
    if (m === which) { unmark(card.item.id); return }
    void mark(card.item.id, which, ctx).catch(() => setErr(true))
  }
  const btn = (on: boolean) =>
    `gh-chip press inline-flex min-h-11 min-w-0 items-center justify-center gap-1 whitespace-nowrap px-1.5 text-[13px] font-semibold leading-[18px] ring-1 ring-inset transition-colors ${
      on ? 'bg-accent-soft text-accent-ink ring-2 ring-accent' : 'bg-transparent text-ink-2 ring-line hover:text-ink'}`
  const label = (k: 'more' | 'less' | 'nfm') => tf((full ? `tf.${k}` : `tf.${k}_short`) as TfKey)
  return (
    <div data-review-id="taste-actions">
      <div className="mt-2 grid grid-cols-3 gap-1.5">
        <button type="button" data-review-id="taste-more" aria-pressed={m === 'more'} onClick={() => tap('more')} className={btn(m === 'more')}>
          {m === 'more' && <Check size={15} strokeWidth={2.5} />}{label('more')}{m === 'more' && <span className="sr-only"> {lf('lf.more_hint')}</span>}
        </button>
        <button type="button" data-review-id="taste-less" aria-pressed={m === 'less'} onClick={() => tap('less')} className={btn(m === 'less')}>
          {m === 'less' && <Check size={15} strokeWidth={2.5} />}{label('less')}{m === 'less' && <span className="sr-only"> {lf('lf.more_hint')}</span>}
        </button>
        <button type="button" data-review-id="taste-nfm" onClick={() => { if (justHid()) return; notForMe(card.item, ctx); onGone?.() }} className={btn(false)}>
          {label('nfm')}
        </button>
      </div>
      {err && <p role="alert" className="t-meta mt-1 text-error">{tf('tf.error')}</p>}
      {full && <HideKind key={card.item.id} item={card.item} /* B-153 swaps the detail in place: a fresh one per activity */ />}
    </div>
  )
}

/** R-94 "Hide this kind" (detail only): its venue, else its source, goes into the Exclude words, with Undo. */
function HideKind({ item }: { item: Card['item'] }) {
  const on = useFlag('words_filter')
  const { exc } = useWords()
  const [did, setDid] = useState<string | null>(null)
  const k = kindOf(item)
  if (!on || !k) return null
  const kind = k.word
  if (did && exc.includes(did)) {
    return (
      <div role="status" data-review-id="hide-kind-done" className="mt-2 flex items-center gap-3">
        <p className="t-meta min-w-0 flex-1 text-ink-2">{fb('fb.hid_kind', { kind: k.label })}</p>
        <button type="button" data-review-id="hide-kind-undo" onClick={() => { unhideKind(did); setDid(null) }} className={buttonClass('secondary', 'md', 'shrink-0 px-4')}>{fb('fb.undo')}</button>
      </div>
    )
  }
  if (exc.includes(kind)) return null // already hidden (from Filters): nothing to offer here
  return (
    <button type="button" data-review-id="hide-kind" onClick={() => { hideKind(kind); setDid(kind) }}
      className="press t-meta mt-1 inline-flex min-h-11 max-w-full items-center text-left text-ink-2 underline underline-offset-4 hover:text-ink">
      <span className="min-w-0 truncate">{fb('fb.hide_kind', { kind: k.label })}</span>
    </button>
  )
}

const REASON_KEY: Record<NfmReason, TfKey> = {
  too_far: 'tf.r.too_far', too_late: 'tf.r.too_late', too_pricey: 'tf.r.too_pricey', wrong_day: 'tf.r.wrong_day',
  been_there: 'tf.r.been_there', sold_out: 'tf.r.sold_out', no_time: 'tf.r.no_time', too_crowded: 'tf.r.too_crowded',
  wrong_vibe: 'tf.r.wrong_vibe', not_my_thing: 'tf.r.not_my_thing', too_touristy: 'tf.r.too_touristy', seen_enough_of_this: 'tf.r.seen_enough',
}
const SHOW_MS = 10_000
const THANKS_MS = 1600

/**
 * After a Not for me: a panel from the bottom says it is gone, offers Undo (B-120) and the optional reason (logistics first,
 * then taste), one tap. It leaves by itself after 10 s (any touch inside restarts the clock), 1.6 s after a reason or a
 * working Undo, or on close; it waits while an Undo is on its way.
 */
export function NotForMePanel() {
  const { last } = useTaste()
  const reduce = useReducedMotion() ?? false
  const [clock, setClock] = useState(0)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => {
    window.clearTimeout(timer.current)
    if (!last || last.status === 'undoing') return
    const wait = last.status === 'back' || (last.reason && last.status === 'sent') ? THANKS_MS : last.status === 'error' || last.status === 'undo_error' ? SHOW_MS * 2 : SHOW_MS
    timer.current = window.setTimeout(dismissLast, wait)
    return () => window.clearTimeout(timer.current)
  }, [last, clock])
  return (
    <AnimatePresence>
      {last && (
        <motion.div key={last.id} data-review-id="nfm-panel" role="region" aria-label={tf('tf.nfm')} onPointerDown={() => setClock((c) => c + 1)}
          initial={reduce ? { opacity: 0 } : { y: 120 }} animate={{ y: 0, opacity: 1 }} exit={reduce ? { opacity: 0 } : { y: 160 }}
          transition={reduce ? FADE.enter : spring('layout')}
          className="fixed inset-x-0 bottom-[calc(56px+env(safe-area-inset-bottom))] z-50 border-t border-line bg-surface px-5 pt-3 pb-4 shadow-floating lg:inset-x-auto lg:right-8 lg:bottom-8 lg:w-[440px] lg:rounded-[20px] lg:ring-1 lg:ring-line">
          <div className="mx-auto w-full max-w-md md:max-w-[640px] lg:max-w-none">
            <div className="flex items-start gap-2">
              <p aria-live="polite" data-sentry-mask className="t-control min-w-0 flex-1 pt-2.5 text-ink">
                {last.status === 'back' ? tf('tf.nfm_back', { title: last.title }) : tf('tf.nfm_gone', { title: last.title })}
              </p>
              {last.status !== 'back' && (
                <button type="button" data-review-id="nfm-undo" onClick={() => void undoLast()} disabled={last.status === 'undoing'} aria-busy={last.status === 'undoing' || undefined}
                  className={buttonClass('secondary', 'md', 'shrink-0 px-4')}>
                  {last.status === 'undoing' && <Spinner size={16} label={false} className="text-current" />}{tf('tf.nfm_undo')}
                </button>
              )}
              <button type="button" aria-label={tf('tf.close')} onClick={dismissLast} className="press -mr-2 inline-flex size-11 shrink-0 items-center justify-center rounded-[12px] text-ink-2 hover:bg-sunk">
                <Close size={18} />
              </button>
            </div>
            {last.status === 'back' ? null : last.status === 'undo_error' ? (
              <p role="alert" className="t-meta mt-2 text-error">{tf('tf.nfm_undo_error')}</p>
            ) : last.status === 'error' ? (
              <div className="mt-2 flex items-center gap-3">
                <p role="alert" className="t-meta min-w-0 flex-1 text-error">{tf('tf.error')}</p>
                <button type="button" onClick={retryLast} className={buttonClass('secondary', 'md')}>{f2('common.retry')}</button>
              </div>
            ) : last.reason ? (
              <p role="status" className="mt-2 flex h-10 items-center gap-1.5 text-[15px] font-semibold text-ink"><Check size={18} strokeWidth={2.5} className="text-accent" />{tf('tf.thanks')}</p>
            ) : (
              <>
                <p className="t-meta mt-1 text-ink-2">{tf('tf.why')}</p>
                <div data-review-id="nfm-reasons" className="rail -mx-5 mt-2 h-10 gap-2 px-5 lg:mx-0 lg:h-auto lg:flex-wrap lg:px-0">
                  {NFM_REASONS.map((r) => (
                    <button key={r} type="button" data-reason={r} onClick={() => giveReason(r)}
                      className="press h-10 shrink-0 whitespace-nowrap rounded-full bg-bg px-3.5 text-[14px] font-medium text-ink ring-1 ring-inset ring-line active:bg-sunk">
                      {tf(REASON_KEY[r])}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
